import React, { useEffect, useMemo, useState } from "react";
import {
  createAdminCallRequestService,
  deleteAdminCallRequest,
  deleteAdminCallRequestService,
  getAdminCallRequests,
  getAdminCallRequestServices,
  updateAdminCallRequestService,
  updateAdminCallRequestStatus,
} from "../../../api/callRequestsAdmin";
import { useNotification } from "../../../context/NotificationContext";

const STATUS_OPTIONS = ["Pending", "Approved", "Rejected"];

const EMPTY_SERVICE_FORM = {
  name: "",
  isActive: true,
};

const CallsRequestedManager = () => {
  const { notify } = useNotification();

  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [serviceCreateForm, setServiceCreateForm] = useState(EMPTY_SERVICE_FORM);
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [serviceEditForm, setServiceEditForm] = useState(EMPTY_SERVICE_FORM);

  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("Pending");

  const loadServices = async () => {
    setServicesLoading(true);
    try {
      const data = await getAdminCallRequestServices();
      setServices(Array.isArray(data) ? data : []);
    } catch (error) {
      notify({
        type: "error",
        title: "Failed to load service options",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setServicesLoading(false);
    }
  };

  const loadRequests = async () => {
    setRequestsLoading(true);
    try {
      const data = await getAdminCallRequests({ status: statusFilter || undefined });
      setRequests(Array.isArray(data) ? data : []);
    } catch (error) {
      notify({
        type: "error",
        title: "Failed to load call requests",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setRequestsLoading(false);
    }
  };

  useEffect(() => {
    loadServices();
  }, []);

  useEffect(() => {
    loadRequests();
  }, [statusFilter]);

  const sortedServices = useMemo(
    () => [...services].sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0)),
    [services]
  );

  const sortedRequests = useMemo(
    () =>
      [...requests].sort(
        (a, b) => new Date(b?.createdAtUtc || 0).getTime() - new Date(a?.createdAtUtc || 0).getTime()
      ),
    [requests]
  );

  const createService = async () => {
    if (!String(serviceCreateForm.name || "").trim()) {
      notify({ type: "error", title: "Service name is required" });
      return;
    }
    try {
      await createAdminCallRequestService({
        name: String(serviceCreateForm.name || "").trim(),
        isActive: Boolean(serviceCreateForm.isActive),
      });
      notify({ type: "success", title: "Service option created" });
      setServiceCreateForm(EMPTY_SERVICE_FORM);
      await loadServices();
    } catch (error) {
      notify({
        type: "error",
        title: "Create failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  const startServiceEdit = (item) => {
    setEditingServiceId(item.id);
    setServiceEditForm({
      name: item?.name || "",
      isActive: typeof item?.isActive === "boolean" ? item.isActive : true,
    });
  };

  const saveServiceEdit = async () => {
    if (!editingServiceId) return;
    if (!String(serviceEditForm.name || "").trim()) {
      notify({ type: "error", title: "Service name is required" });
      return;
    }
    try {
      await updateAdminCallRequestService({
        id: editingServiceId,
        payload: {
          name: String(serviceEditForm.name || "").trim(),
          isActive: Boolean(serviceEditForm.isActive),
        },
      });
      notify({ type: "success", title: "Service option updated" });
      setEditingServiceId(null);
      setServiceEditForm(EMPTY_SERVICE_FORM);
      await loadServices();
    } catch (error) {
      notify({
        type: "error",
        title: "Update failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  const removeService = async (item) => {
    if (!window.confirm(`Delete service option "${item?.name}"?`)) return;
    try {
      await deleteAdminCallRequestService({ id: item.id });
      notify({ type: "success", title: "Service option deleted" });
      await loadServices();
    } catch (error) {
      notify({
        type: "error",
        title: "Delete failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  const updateRequestStatus = async (item, status) => {
    try {
      await updateAdminCallRequestStatus({ id: item.id, status });
      notify({ type: "success", title: `Request marked as ${status}` });
      await loadRequests();
    } catch (error) {
      notify({
        type: "error",
        title: "Status update failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  const removeRequest = async (item) => {
    if (!window.confirm(`Delete call request #${item?.id}?`)) return;
    try {
      await deleteAdminCallRequest({ id: item.id });
      notify({ type: "success", title: "Call request deleted" });
      await loadRequests();
    } catch (error) {
      notify({
        type: "error",
        title: "Delete failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  return (
    <section className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#011337]">Call Request Services</h2>
            <p className="text-sm text-slate-500 mt-1">Create and manage service options used in call request forms.</p>
          </div>
          <button
            type="button"
            onClick={loadServices}
            className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm"
          >
            Reload
          </button>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <label className="text-sm text-slate-700 sm:col-span-2">
            Service Name
            <input
              type="text"
              value={serviceCreateForm.name}
              onChange={(e) => setServiceCreateForm((p) => ({ ...p, name: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 mt-7">
            <input
              type="checkbox"
              checked={Boolean(serviceCreateForm.isActive)}
              onChange={(e) => setServiceCreateForm((p) => ({ ...p, isActive: e.target.checked }))}
            />
            Active
          </label>
        </div>
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={createService}
            className="px-4 py-2 rounded-lg bg-[#FE7E3F] text-white hover:bg-[#FE7E3F]/90 text-sm"
          >
            Create service option
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white">
        <table className="min-w-full table-fixed">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">ID</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {servicesLoading ? (
              <tr><td colSpan={4} className="px-3 py-4 text-sm text-slate-500">Loading service options...</td></tr>
            ) : sortedServices.length ? (
              sortedServices.map((item) => (
                <tr key={item.id} className="border-b border-slate-200 align-top">
                  <td className="px-3 py-3 text-sm text-slate-700">{item.id}</td>
                  <td className="px-3 py-3 text-sm font-medium text-slate-900">{item.name || "-"}</td>
                  <td className="px-3 py-3 text-sm">
                    <span className={`px-2 py-1 rounded text-xs ${item.isActive ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>
                      {item.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => startServiceEdit(item)}
                        className="text-xs px-2 py-1.5 rounded border border-[#011337] text-[#011337] hover:bg-[#011337]/10"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => removeService(item)}
                        className="text-xs px-2 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={4} className="px-3 py-4 text-sm text-slate-500">No service options found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editingServiceId ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="text-lg font-semibold text-[#011337]">Edit Service Option #{editingServiceId}</h3>
            <button
              type="button"
              onClick={() => {
                setEditingServiceId(null);
                setServiceEditForm(EMPTY_SERVICE_FORM);
              }}
              className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50"
            >
              Close
            </button>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <label className="text-sm text-slate-700 sm:col-span-2">
              Service Name
              <input
                type="text"
                value={serviceEditForm.name}
                onChange={(e) => setServiceEditForm((p) => ({ ...p, name: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 mt-7">
              <input
                type="checkbox"
                checked={Boolean(serviceEditForm.isActive)}
                onChange={(e) => setServiceEditForm((p) => ({ ...p, isActive: e.target.checked }))}
              />
              Active
            </label>
          </div>
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={saveServiceEdit}
              className="px-4 py-2 rounded-lg bg-[#011337] text-white hover:bg-[#011337]/90 text-sm"
            >
              Save changes
            </button>
          </div>
        </div>
      ) : null}

      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#011337]">Call Requests</h2>
            <p className="text-sm text-slate-500 mt-1">Review incoming call requests, update status, or delete them.</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={loadRequests}
              className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm"
            >
              Reload
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white">
        <table className="min-w-full table-fixed">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">ID</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Service</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Phone</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {requestsLoading ? (
              <tr><td colSpan={6} className="px-3 py-4 text-sm text-slate-500">Loading call requests...</td></tr>
            ) : sortedRequests.length ? (
              sortedRequests.map((item) => (
                <tr key={item.id} className="border-b border-slate-200 align-top">
                  <td className="px-3 py-3 text-sm text-slate-700">{item.id}</td>
                  <td className="px-3 py-3 text-sm text-slate-900">
                    <p className="font-medium">{item.name || "-"}</p>
                    {item.email ? <p className="text-xs text-slate-500 mt-1 break-all">{item.email}</p> : null}
                    {item.message ? <p className="text-xs text-slate-500 mt-1">{item.message}</p> : null}
                  </td>
                  <td className="px-3 py-3 text-sm text-slate-700">{item.serviceName || "-"}</td>
                  <td className="px-3 py-3 text-sm text-slate-700">{item.phoneNumber || "-"}</td>
                  <td className="px-3 py-3 text-sm">
                    <span className={`px-2 py-1 rounded text-xs ${
                      item.status === "Approved"
                        ? "bg-emerald-100 text-emerald-800"
                        : item.status === "Rejected"
                        ? "bg-rose-100 text-rose-700"
                        : "bg-amber-100 text-amber-800"
                    }`}>
                      {item.status || "Pending"}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-2">
                      {STATUS_OPTIONS.map((status) => (
                        <button
                          key={status}
                          type="button"
                          onClick={() => updateRequestStatus(item, status)}
                          className="text-xs px-2 py-1.5 rounded border border-slate-300 hover:bg-slate-50"
                        >
                          {status}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => removeRequest(item)}
                        className="text-xs px-2 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={6} className="px-3 py-4 text-sm text-slate-500">No call requests found for selected status.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default CallsRequestedManager;
